// Viloyat/tuman ma'lumotnomasi — `platform` `geo` moduli bilan kontrakt.
//
//   GET    /api/regions?q&activeOnly&page&size&sort
//   POST   /api/regions            {name, code?}
//   PUT    /api/regions/{id}       {name, code?, active}
//   DELETE /api/regions/{id}       -> yumshoq; ishlatilsa 409
//   GET    /api/districts?regionId&q&activeOnly&page&size&sort
//   POST   /api/districts          {regionId, name, code?}
//   PUT    /api/districts/{id}     {regionId, name, code?, active}
//   DELETE /api/districts/{id}     -> yumshoq; ishlatilsa 409
//
// Huquqlar: o'qish `geo.read`, yozish `geo.write`.
//
// ⚠️ Sahifa 0 dan sanaladi (`/api/sites` bilan bir xil andoza), `TableState.page`
// esa 1 dan — chegara aynan shu faylda o'giriladi.
//
// ⚠️ `total` YOKI `hasNext` — ikkalasi ham qabul qilinadi. Qo'shni resurs
// (`/api/sites`) `total` beradi, `ledger`/`audit` esa faqat `hasNext`. Sxema
// bittasini talab qilsa, backend ikkinchi shaklda javob berganda jadval
// yiqilardi.

import { z } from 'zod'
import { api } from '@/shared/api/client'
import { syntheticTotal } from '@/shared/api/pageResult'
import { instantSchema, uuidSchema } from '@/shared/api/schemas'
import type { PageResult } from '@/shared/ui/table/DataTable'
import type { TableState } from '@/shared/ui/table/tableState'

export interface Region {
  id: string
  name: string
  code: string | null
  active: boolean
  createdAt: string | null
}

export interface District extends Region {
  regionId: string
  /** Server bermasa `null` — nom mijozdagi viloyat xaritasidan olinadi */
  regionName: string | null
}

interface RegionDraft {
  name: string
  code: string | null
}

interface DistrictDraft extends RegionDraft {
  regionId: string
}

const regionSchema = z.object({
  id: uuidSchema,
  name: z.string(),
  code: z.string().nullish(),
  active: z.boolean(),
  createdAt: instantSchema.nullish(),
})

const districtSchema = regionSchema.extend({
  regionId: uuidSchema,
  regionName: z.string().nullish(),
})

function pageSchema<T extends z.ZodTypeAny>(row: T) {
  return z.object({
    rows: z.array(row),
    total: z.number().nullish(),
    hasNext: z.boolean().nullish(),
  })
}

function toRegion(raw: z.infer<typeof regionSchema>): Region {
  return {
    id: raw.id,
    name: raw.name,
    code: raw.code ?? null,
    active: raw.active,
    createdAt: raw.createdAt ?? null,
  }
}

function toDistrict(raw: z.infer<typeof districtSchema>): District {
  return { ...toRegion(raw), regionId: raw.regionId, regionName: raw.regionName ?? null }
}

/** URL filtri kalitlari — jadval ustuni va `GeoFilterBar` bir xil nom ishlatadi */
export const ACTIVE_FILTER = 'active'
export const REGION_FILTER = 'region'

/**
 * Holat filtrining qiymatlari.
 *
 * ⚠️ SUKUT — `ACTIVE_ONLY` (egasi qarori 2026-08-20): ro'yxatda faqat
 * AMALDAGI yozuvlar ko'rinadi. Ilgari filtr bo'sh bo'lsa server BARCHA
 * yozuvni qaytarardi va nofaol viloyatlar faol bo'lganlar orasida
 * aralashib yotardi.
 *
 * ⚠️ `SHOW_ALL` — nofaolni topishning YAGONA yo'li, chunki kontraktda
 * `activeOnly` bayrog'idan boshqa hech narsa yo'q: «faqat nofaollar»
 * degan so'rov serverda MAVJUD EMAS. Uni mijozda yasash sahifalangan
 * ro'yxatda faqat joriy sahifani toraytirardi.
 */
export const ACTIVE_ONLY = 'active'
export const SHOW_ALL = 'all'

/** Sukut tartib — egasi qoidasi: oxirgi qo'shilgan birinchi */
const DEFAULT_SORT = 'createdAt,desc'

/** Ustun kaliti → server maydoni. Ro'yxatda yo'q ustun saralanmaydi. */
const SORT_FIELDS: Readonly<Record<string, string>> = {
  name: 'name',
  created: 'createdAt',
}

function sortParam(state: TableState): string {
  const field = state.sortField === null ? undefined : SORT_FIELDS[state.sortField]
  if (field === undefined || state.sortOrder === null) return DEFAULT_SORT
  return `${field},${state.sortOrder === 'ascend' ? 'asc' : 'desc'}`
}

/**
 * So'rov parametrlari.
 *
 * ⚠️ ATAYLAB EKSPORT QILINGAN va `fetch*Page` dan AJRATILGAN: sukut
 * qiymatni («belgilanmagan = faqat faollar») CHAQIRIB o'lchash kerak.
 * Manba matnida `activeOnly` naqshini qidirish bu farqni ko'rmasdi —
 * bayroq joyida turib, sukut teskari bo'lishi mumkin
 * (`tests/closed-default.mjs`).
 */
export function geoListParams(state: TableState): Record<string, unknown> {
  const q = state.search.trim()
  return {
    ...(q === '' ? {} : { q }),
    ...(state.filters[ACTIVE_FILTER]?.[0] === SHOW_ALL ? {} : { activeOnly: true }),
    page: state.page - 1,
    size: state.pageSize,
    sort: sortParam(state),
  }
}

function toPageResult<T>(
  parsed: { rows: readonly unknown[]; total?: number | null; hasNext?: boolean | null },
  items: readonly T[],
  state: TableState,
): PageResult<T> {
  const total =
    parsed.total ??
    syntheticTotal(state.page, state.pageSize, items.length, parsed.hasNext ?? false)
  return { items, total }
}

export async function fetchRegionPage(
  state: TableState,
  signal: AbortSignal,
): Promise<PageResult<Region>> {
  const response = await api.get<unknown>('/api/regions', { signal, params: geoListParams(state) })
  const parsed = pageSchema(regionSchema).parse(response.data)
  return toPageResult(parsed, parsed.rows.map(toRegion), state)
}

export async function fetchDistrictPage(
  state: TableState,
  signal: AbortSignal,
): Promise<PageResult<District>> {
  const regionId = state.filters[REGION_FILTER]?.[0]
  const response = await api.get<unknown>('/api/districts', {
    signal,
    params: { ...geoListParams(state), ...(regionId ? { regionId } : {}) },
  })
  const parsed = pageSchema(districtSchema).parse(response.data)
  return toPageResult(parsed, parsed.rows.map(toDistrict), state)
}

export async function createRegion(draft: RegionDraft): Promise<Region> {
  const response = await api.post<unknown>('/api/regions', draft)
  return toRegion(regionSchema.parse(response.data))
}

export async function updateRegion(
  id: string,
  draft: RegionDraft & { active: boolean },
): Promise<Region> {
  const response = await api.put<unknown>(`/api/regions/${id}`, draft)
  return toRegion(regionSchema.parse(response.data))
}

/** Yumshoq o'chirish. Ishlatilayotgan viloyat 409 beradi va sabab xabarda. */
export async function deactivateRegion(id: string): Promise<void> {
  await api.delete<unknown>(`/api/regions/${id}`)
}

export async function createDistrict(draft: DistrictDraft): Promise<District> {
  const response = await api.post<unknown>('/api/districts', draft)
  return toDistrict(districtSchema.parse(response.data))
}

export async function updateDistrict(
  id: string,
  draft: DistrictDraft & { active: boolean },
): Promise<District> {
  const response = await api.put<unknown>(`/api/districts/${id}`, draft)
  return toDistrict(districtSchema.parse(response.data))
}

export async function deactivateDistrict(id: string): Promise<void> {
  await api.delete<unknown>(`/api/districts/${id}`)
}

interface GeoOption {
  id: string
  name: string
  active: boolean
}

export interface GeoOptions {
  options: readonly GeoOption[]
  /** Ro'yxat chegaradan oshdi — tanlovda hamma viloyat/tuman YO'Q */
  truncated: boolean
}

const OPTIONS_SIZE = 300

function toOptions(
  parsed: { rows: readonly { id: string; name: string; active: boolean }[]; total?: number | null; hasNext?: boolean | null },
): GeoOptions {
  return {
    options: parsed.rows.map((row) => ({ id: row.id, name: row.name, active: row.active })),
    truncated: parsed.hasNext ?? (parsed.total ?? 0) > parsed.rows.length,
  }
}

export async function fetchRegionOptions(activeOnly: boolean): Promise<GeoOptions> {
  const response = await api.get<unknown>('/api/regions', {
    params: { page: 0, size: OPTIONS_SIZE, sort: 'name,asc', ...(activeOnly ? { activeOnly: true } : {}) },
  })
  return toOptions(pageSchema(regionSchema).parse(response.data))
}

export async function fetchDistrictOptions(
  regionId: string,
  activeOnly: boolean,
): Promise<GeoOptions> {
  const response = await api.get<unknown>('/api/districts', {
    params: {
      regionId,
      page: 0,
      size: OPTIONS_SIZE,
      sort: 'name,asc',
      ...(activeOnly ? { activeOnly: true } : {}),
    },
  })
  return toOptions(pageSchema(districtSchema).parse(response.data))
}
